import React, { useState, useEffect, useMemo } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useAuth } from '@/features/auth/AuthContext'
import { COMPANY_PROFILE } from '@/config/companyProfile'
import { formatDate } from '@/lib/dateFormat'
import { Button } from '@/components/ui/Button'
import {
  fetchProductionOrders,
  type ProductionOrder,
} from '@/services/productionOrders'
import {
  filterCompanyProfileForPrint,
  formatDimPure,
} from './productionOrderUtils'

export function ProductionOrdersPrintPage({
  initialOrders,
}: {
  initialOrders?: ProductionOrder[]
} = {}): React.ReactElement {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { user } = useAuth()

  const month = searchParams.get('month') || new Date().toISOString().slice(0, 7)
  const [orders, setOrders] = useState<ProductionOrder[]>(initialOrders || [])
  const [loading, setLoading] = useState(initialOrders === undefined)
  const [showImages, setShowImages] = useState(false)

  // Current print timestamp
  const now = useMemo(() => new Date(), [])
  const printDateStr = useMemo(() => {
    const pad = (n: number) => String(n).padStart(2, '0')
    const day = pad(now.getDate())
    const m = pad(now.getMonth() + 1)
    const y = now.getFullYear()
    const hh = pad(now.getHours())
    const mm = pad(now.getMinutes())
    return `${day}/${m}/${y} ${hh}:${mm}`
  }, [now])

  // Company profile filtered to exclude [Điền...] placeholders
  const cleanProfile = useMemo(() => filterCompanyProfileForPrint(COMPANY_PROFILE), [])

  useEffect(() => {
    if (initialOrders !== undefined) return
    setLoading(true)
    fetchProductionOrders({ month })
      .then((res: ProductionOrder[]) => setOrders(res))
      .catch(() => setOrders([]))
      .finally(() => setLoading(false))
  }, [month, initialOrders])

  // Month display title (e.g. 2026-09 -> Tháng 09/2026)
  const monthDisplay = useMemo(() => {
    const parts = month.split('-')
    if (parts.length === 2) {
      return `Tháng ${parts[1]}/${parts[0]}`
    }
    return month
  }, [month])

  const totalPlannedQty = useMemo(
    () => orders.reduce((sum, o) => sum + (o.planned_qty || 0), 0),
    [orders],
  )

  return (
    <div className="min-h-screen bg-slate-100 p-4 print:p-0 print:bg-white text-slate-900 font-sans">
      {/* Print styles injected for A4 landscape */}
      <style>{`
        @page {
          size: A4 landscape;
          margin: 8mm 10mm 8mm 10mm;
        }
        @media print {
          body {
            background: #ffffff !important;
            font-size: 11px !important;
            color: #000000 !important;
            font-family: var(--font-sans), 'Inter', sans-serif !important;
          }
          .no-print {
            display: none !important;
          }
          .print-container {
            max-width: 100% !important;
            box-shadow: none !important;
            border: none !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          thead {
            display: table-header-group;
          }
          tr {
            page-break-inside: avoid;
            break-inside: avoid;
          }
        }
      `}</style>

      {/* Control Bar (hidden in print) */}
      <div className="no-print mx-auto mb-4 flex max-w-6xl items-center justify-between rounded-lg border border-slate-300 bg-white p-3 shadow-sm">
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate(`/planning/production-orders?month=${month}`)}
          >
            ← Quay lại
          </Button>
          <span className="font-bold text-slate-800 text-sm">
            In Lệnh sản xuất ({monthDisplay})
          </span>
        </div>

        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-xs font-medium text-slate-700 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showImages}
              onChange={(e) => setShowImages(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
            />
            <span>Kèm ảnh sản phẩm</span>
          </label>

          <Button
            variant="primary"
            size="sm"
            onClick={() => window.print()}
            className="bg-emerald-700 hover:bg-emerald-800 text-white font-semibold"
          >
            🖨 In ngay (Ctrl+P)
          </Button>
        </div>
      </div>

      {/* Printable Sheet */}
      <div className="print-container mx-auto max-w-6xl rounded-lg border border-slate-300 bg-white p-6 shadow-md print:border-none print:shadow-none print:p-0">
        {/* Document Header */}
        <div className="border-b-2 border-slate-800 pb-3 mb-3">
          <div className="flex items-start justify-between">
            {/* Company logo + info */}
            <div className="flex items-start gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded bg-white">
                <img
                  src="/logo-mocphat.jpg"
                  alt="Mộc Phát"
                  className="h-full w-full object-contain"
                />
              </div>
              <div>
                {cleanProfile.legalName && (
                  <h2 className="text-sm font-extrabold uppercase tracking-wide text-slate-900">
                    {cleanProfile.legalName}
                  </h2>
                )}
                {cleanProfile.brandName && (
                  <p className="text-xs font-semibold text-slate-700">
                    {cleanProfile.brandName} {cleanProfile.brandTagline || ''}
                  </p>
                )}
                {cleanProfile.address && (
                  <p className="text-[11px] text-slate-600">{cleanProfile.address}</p>
                )}
                {cleanProfile.phone && (
                  <p className="text-[11px] text-slate-600">ĐT: {cleanProfile.phone}</p>
                )}
              </div>
            </div>

            {/* Document Meta */}
            <div className="text-right text-[11px] text-slate-600 space-y-0.5">
              <p>
                <strong>Ngày in:</strong> {printDateStr}
              </p>
              <p>
                <strong>Người in:</strong> {user?.username || 'admin'}
              </p>
            </div>
          </div>

          {/* Title */}
          <div className="text-center mt-2">
            <h1 className="text-xl font-black uppercase tracking-wider text-slate-900">
              LỆNH SẢN XUẤT
            </h1>
            <p className="text-xs font-bold text-emerald-800 tracking-wide mt-0.5">
              {monthDisplay.toUpperCase()}
            </p>
          </div>
        </div>

        {/* Content Table */}
        {loading ? (
          <p className="py-12 text-center text-xs text-slate-500 italic">Đang tải dữ liệu...</p>
        ) : orders.length === 0 ? (
          <p className="py-12 text-center text-xs text-slate-500 italic">
            Không có lệnh sản xuất nào trong tháng {month}.
          </p>
        ) : (
          <table className="w-full border-collapse text-[10px] leading-tight">
            <thead>
              <tr className="bg-slate-100 text-slate-900 font-bold border-t border-b-2 border-slate-700 text-center">
                <th className="border border-slate-300 px-1 py-1 w-7">STT</th>
                <th className="border border-slate-300 px-1 py-1 w-14">Khách</th>
                {showImages && (
                  <th className="border border-slate-300 px-1 py-1 w-12">Ảnh</th>
                )}
                <th className="border border-slate-300 px-1.5 py-1 w-24">Mã hàng</th>
                <th className="border border-slate-300 px-1.5 py-1 text-left min-w-[130px]">Tên sản phẩm</th>
                <th className="border border-slate-300 px-1 py-1 w-7">W</th>
                <th className="border border-slate-300 px-1 py-1 w-7">D</th>
                <th className="border border-slate-300 px-1 py-1 w-7">H</th>
                <th className="border border-slate-300 px-1 py-1 w-14">Ráp/Tháo</th>
                <th className="border border-slate-300 px-1 py-1 w-8">ISTA</th>
                <th className="border border-slate-300 px-1.5 py-1 w-14 text-right">SL Kế hoạch</th>
                <th className="border border-slate-300 px-1 py-1 w-20">ĐV Mộc</th>
                <th className="border border-slate-300 px-1 py-1 w-20">ĐV Sơn</th>
                <th className="border border-slate-300 px-1 py-1 w-16">NVL về</th>
                <th className="border border-slate-300 px-1 py-1 w-16">Xong mộc</th>
                <th className="border border-slate-300 px-1 py-1 w-16">Xong sơn</th>
                <th className="border border-slate-300 px-1.5 py-1 w-18 font-extrabold bg-slate-200">Hoàn thành NM</th>
                <th className="border border-slate-300 px-1 py-1 w-16">Giao (ETD)</th>
                <th className="border border-slate-300 px-1.5 py-1 text-left min-w-[90px]">Ghi chú</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order, idx) => (
                <tr key={order.id} className="border-b border-slate-200 hover:bg-slate-50">
                  <td className="border border-slate-300 px-1 py-1 text-center font-mono">{idx + 1}</td>
                  <td className="border border-slate-300 px-1 py-1 font-bold font-mono text-center truncate">
                    {order.customer_code || '—'}
                  </td>
                  {showImages && (
                    <td className="border border-slate-300 p-0.5 text-center">
                      {order.product_id ? (
                        <img
                          src={`/api/products/${encodeURIComponent(order.product_id)}/image`}
                          alt={order.variant_code}
                          className="mx-auto h-8 w-8 object-contain"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none'
                          }}
                        />
                      ) : (
                        <span className="text-slate-300">📦</span>
                      )}
                    </td>
                  )}
                  <td className="border border-slate-300 px-1.5 py-1 font-mono font-bold text-slate-900 table-cell-wrap bom-wrap-text">
                    {order.variant_code}
                  </td>
                  <td className="border border-slate-300 px-1.5 py-1 text-slate-800 table-cell-wrap bom-wrap-text" title={order.product_name}>
                    {order.product_name}
                  </td>
                  <td className="border border-slate-300 px-1 py-1 text-right font-mono">
                    {formatDimPure(order.w, 'inch')}
                  </td>
                  <td className="border border-slate-300 px-1 py-1 text-right font-mono">
                    {formatDimPure(order.d, 'inch')}
                  </td>
                  <td className="border border-slate-300 px-1 py-1 text-right font-mono">
                    {formatDimPure(order.h, 'inch')}
                  </td>
                  <td className="border border-slate-300 px-1 py-1 text-center">
                    {order.assembly_type === 'assembled' ? 'Ráp chết' : 'Tháo rời'}
                  </td>
                  <td className="border border-slate-300 px-1 py-1 text-center font-mono">
                    {order.ista_level || '—'}
                  </td>
                  <td className="border border-slate-300 px-1.5 py-1 text-right font-mono font-bold">
                    {Number(order.planned_qty || 0).toLocaleString('vi-VN')}
                  </td>
                  <td className="border border-slate-300 px-1 py-1 table-cell-wrap bom-wrap-text">{order.wood_unit_name || '—'}</td>
                  <td className="border border-slate-300 px-1 py-1 table-cell-wrap bom-wrap-text">{order.paint_unit_name || '—'}</td>
                  <td className="border border-slate-300 px-1 py-1 text-center font-mono">
                    {formatDate(order.d_material_arrival)}
                  </td>
                  <td className="border border-slate-300 px-1 py-1 text-center font-mono">
                    {formatDate(order.d_white_done)}
                  </td>
                  <td className="border border-slate-300 px-1 py-1 text-center font-mono">
                    {formatDate(order.d_paint_done)}
                  </td>
                  <td className="border border-slate-300 px-1.5 py-1 text-center font-mono font-bold bg-slate-50">
                    {formatDate(order.d_factory_completion)}
                  </td>
                  <td className="border border-slate-300 px-1 py-1 text-center font-mono">
                    {formatDate(order.d_shipment)}
                  </td>
                  <td className="border border-slate-300 px-1.5 py-1 table-cell-wrap bom-wrap-text text-slate-700" title={order.note || ''}>
                    {order.note || '—'}
                  </td>
                </tr>
              ))}

              {/* Total Summary Row */}
              <tr className="bg-slate-100 font-bold border-t-2 border-slate-800">
                <td colSpan={showImages ? 10 : 9} className="border border-slate-300 px-2 py-1.5 text-right font-bold uppercase">
                  Tổng cộng:
                </td>
                <td className="border border-slate-300 px-1.5 py-1.5 text-right font-mono font-extrabold text-emerald-900">
                  {Number(totalPlannedQty).toLocaleString('vi-VN')}
                </td>
                <td colSpan={8} className="border border-slate-300 px-2 py-1.5 text-slate-500 font-normal">
                  ({orders.length} dòng sản xuất)
                </td>
              </tr>
            </tbody>
          </table>
        )}

        {/* 3-Party Signature Block */}
        <div className="mt-8 pt-4 border-t border-slate-300 grid grid-cols-3 gap-8 text-center text-xs">
          <div>
            <p className="font-bold uppercase tracking-wider text-slate-800">Người lập</p>
            <p className="text-[10px] text-slate-500 italic mt-0.5">(Ký, ghi rõ họ tên)</p>
            <div className="h-16" />
            <p className="font-medium text-slate-700">{user?.username || ''}</p>
          </div>

          <div>
            <p className="font-bold uppercase tracking-wider text-slate-800">Phòng Kế hoạch</p>
            <p className="text-[10px] text-slate-500 italic mt-0.5">(Ký, ghi rõ họ tên)</p>
            <div className="h-16" />
          </div>

          <div>
            <p className="font-bold uppercase tracking-wider text-slate-800">Giám đốc</p>
            <p className="text-[10px] text-slate-500 italic mt-0.5">(Ký, đóng dấu)</p>
            <div className="h-16" />
          </div>
        </div>
      </div>
    </div>
  )
}
